import React, { useEffect, useRef, useState } from "react";
import Header from "../../Components/Header/Header";
import Footer from "../../Components/Footer/Footer";
import s from "./Test.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import {
  getTestSections,
  getTestProgress,
  submitTestAnswer,
  completeTest,
  getTestResults,
  resetTest,
} from "../../redux/InformationSlice";
import { Flex, Progress } from "antd";
import { useNavigate } from "react-router-dom";
import ScrollToTop from "../../Components/ScrollToTop/ScrollToTop";
import useWindowDimensions from "../../Components/WindowDimensions/WindowDimensions";
import MobileHeader from "../../Components/MobileHeader/MobileHeader";
import { useTranslation } from "react-i18next";

const Test = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { testSections, testProgress } = useSelector(
    (state: RootState) => state.info,
  );

  const footerRef = useRef<HTMLDivElement | null>(null);
  const scrollToFooter = () => {
    footerRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<
    Record<number, number>
  >({});
  const [loading, setLoading] = useState(false);
  const { width } = useWindowDimensions();

  // Initialize: Load sections and check if we need to reset
  useEffect(() => {
    const initializeTest = async () => {
      await dispatch(getTestSections());
      const progressResult = await dispatch(getTestProgress());

      // If test was completed, auto-reset to start fresh
      if (
        progressResult.payload &&
        (progressResult.payload as any).is_completed
      ) {
        await dispatch(resetTest());
        await dispatch(getTestProgress());
        // Reset UI state
        setCurrentSectionIndex(0);
        setSelectedAnswers({});
      }
    };

    initializeTest();
  }, [dispatch]);

  // Refresh progress when answers change
  useEffect(() => {
    if (selectedAnswers && Object.keys(selectedAnswers).length > 0) {
      dispatch(getTestProgress());
    }
  }, [selectedAnswers, dispatch]);

  const handleAnswerSelect = async (
    questionId: number,
    answerOptionId: number,
  ) => {
    setSelectedAnswers((prev) => ({ ...prev, [questionId]: answerOptionId }));
    try {
      await dispatch(
        submitTestAnswer({
          question_id: questionId,
          answer_option_id: answerOptionId,
        }),
      ).unwrap();
      // Refresh progress after submitting answer
      dispatch(getTestProgress());
    } catch (error) {
      console.error("Failed to submit answer:", error);
      // Revert selection on error
      setSelectedAnswers((prev) => {
        const newState = { ...prev };
        delete newState[questionId];
        return newState;
      });
    }
  };

  const handleNext = () => {
    if (currentSectionIndex < (testSections?.length || 0) - 1) {
      setCurrentSectionIndex(currentSectionIndex + 1);
    }
  };

  const handleBack = () => {
    if (currentSectionIndex > 0) {
      setCurrentSectionIndex(currentSectionIndex - 1);
    }
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      await dispatch(completeTest()).unwrap();
      await dispatch(getTestResults()).unwrap();
      navigate("/test/results");
    } catch (error) {
      console.error("Failed to complete test:", error);
      setLoading(false);
    }
  };

  const currentSection = testSections?.[currentSectionIndex];
  const totalSections = testSections?.length || 0;

  if (!testSections || testSections.length === 0) {
    return (
      <div>
        <div className={s.content}>
          {width > 1100 ? (
            <Header onContactsClick={scrollToFooter} />
          ) : (
            <MobileHeader onContactsClick={scrollToFooter} />
          )}
          <div className={s.loading}>{t("Загрузка...")}</div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div>
      <div className={s.content}>
        {width > 1100 ? (
          <Header onContactsClick={scrollToFooter} />
        ) : (
          <MobileHeader onContactsClick={scrollToFooter} />
        )}
        <div className={s.title}>{t("ВОПРОСЫ")}</div>
        <div className={s.subtitle}>
          {t("Блок вопросов {{current}}/{{total}}", {
            current: currentSectionIndex + 1,
            total: totalSections,
          })}
        </div>

        {currentSection && (
          <div className={s.test}>
            <div className={s.test_title}>{currentSection.title}</div>
            <div className={s.test_subtitle}>{currentSection.description}</div>

            <div className={s.questions}>
              {currentSection.questions.map((question) => (
                <div key={question.id} className={s.question_item}>
                  <div className={s.question_header}>
                    <div className={s.question_number}>
                      {/* {currentSection.code} */}
                      {question.order}
                    </div>
                    <div className={s.question_text}>{question.text}</div>
                  </div>
                  <div className={s.answer_options}>
                    {question.answer_options.map((option) => (
                      <div className={s.option_wrapper}>
                        <label
                          key={option.id}
                          className={`${s.answer_option} ${
                            selectedAnswers[question.id] === option.id
                              ? s.selected
                              : ""
                          }`}
                        >
                          <input
                            type="radio"
                            name={`question-${question.id}`}
                            checked={selectedAnswers[question.id] === option.id}
                            onChange={() =>
                              handleAnswerSelect(question.id, option.id)
                            }
                          />
                          <span>{option.text}</span>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Progress Table */}
        {testProgress && (
          <div className={s.progress_section}>
            <div className={s.progress_title}>
              {t("Мои результаты прохождения теста")}
            </div>
            <div className={s.progress_table}>
              <div className={s.progress_header}>
                <div>{t("Блоки")}</div>
                <div>{t("Ответы")}</div>
                <div>{t("Прогресс")}</div>
              </div>
              {testProgress.sections.map((section) => (
                <div key={section.section_code} className={s.progress_row}>
                  <div className={s.progress_block}>
                    <div className={s.block_code}>{section.section_code}</div>
                  </div>
                  <div className={s.progress_answers}>
                    {section.answered_count}/{section.total_count}
                  </div>
                  <div className={s.progress_percentage}>
                    {section.progress}%
                  </div>
                  <div className={s.progress_bar}>
                    <Flex vertical gap="small" style={{ width: "100%" }}>
                      <Progress
                        percent={section.progress}
                        size="small"
                        showInfo={false}
                        strokeColor="#34c759"
                        strokeWidth={4}
                      />
                    </Flex>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className={s.buttons}>
          <button
            className={s.btn}
            onClick={handleBack}
            disabled={currentSectionIndex === 0}
          >
            <div className={s.btn_icon}>
              <img src="/images/arrow_left.svg" alt="back" />
            </div>
            <div className={s.btn_text}>{t("НАЗАД")}</div>
          </button>

          {currentSectionIndex < totalSections - 1 ? (
            <button className={s.btn} onClick={handleNext}>
              <div className={s.btn_text}>{t("ДАЛЕЕ")}</div>
              <div className={s.btn_icon}>
                <img
                  src="/images/arrow_left.svg"
                  alt="next"
                  style={{ transform: "rotate(180deg)" }}
                />
              </div>
            </button>
          ) : (
            <button
              className={s.btn}
              onClick={handleComplete}
              disabled={loading}
            >
              <div className={s.btn_icon}>
                <img src="/images/link.svg" alt="save" />
              </div>
              <div className={s.btn_text}>{t("СОХРАНИТЬ И ВЫЙТИ")}</div>
            </button>
          )}
        </div>
      </div>
      <div ref={footerRef}>
        <Footer />
      </div>
      <ScrollToTop />
    </div>
  );
};

export default Test;
